import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, Clock, MapPin, Bell, Save, Plus, Edit3, Trash2, X,
  User, Calendar, CheckCircle, AlertCircle, Settings, RefreshCw,
  Sun, Moon, Users, Building2, FileText, Download, Filter, Search
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// مكون الخريطة التفاعلية باستخدام Leaflet
function LocationPicker({ location, onChange, label }) {
  const { t, isRTL } = useLanguage();
  const [searchAddress, setSearchAddress] = useState(location?.address || '');
  const [showMap, setShowMap] = useState(false);
  const [mapKey, setMapKey] = useState(0);

  // قائمة المدن السعودية الرئيسية
  const saudiCities = {
    'الرياض': { latitude: 24.7136, longitude: 46.6753 },
    'جدة': { latitude: 21.4858, longitude: 39.1925 },
    'الدمام': { latitude: 26.4207, longitude: 50.0888 },
    'مكة المكرمة': { latitude: 21.3891, longitude: 39.8579 },
    'المدينة المنورة': { latitude: 24.5247, longitude: 39.5692 },
    'الطائف': { latitude: 21.2854, longitude: 40.4158 },
    'تبوك': { latitude: 28.3838, longitude: 36.5549 },
    'بريدة': { latitude: 26.3260, longitude: 43.9750 },
    'خميس مشيط': { latitude: 18.3093, longitude: 42.7256 },
    'حائل': { latitude: 27.5114, longitude: 41.7208 },
    'نجران': { latitude: 17.4924, longitude: 44.1277 },
    'جازان': { latitude: 16.8893, longitude: 42.5700 },
    'الأحساء': { latitude: 25.3648, longitude: 49.5883 },
    'الخبر': { latitude: 26.2861, longitude: 50.2083 },
    'الظهران': { latitude: 26.3040, longitude: 50.1136 },
  };

  // تحديث الموقع من الإحداثيات
  const handleCoordinateChange = (field, value) => {
    const newLocation = { ...location, [field]: parseFloat(value) || 0 };
    onChange(newLocation);
  };

  // البحث عن عنوان
  const searchLocation = async () => {
    // البحث في المدن السعودية
    for (const [city, coords] of Object.entries(saudiCities)) {
      if (searchAddress.includes(city) || city.includes(searchAddress)) {
        onChange({ ...coords, address: searchAddress, radius: location?.radius || 100 });
        setMapKey(prev => prev + 1);
        return;
      }
    }
    
    // إذا لم يتم العثور على المدينة، استخدم الرياض كافتراضي
    onChange({ 
      latitude: 24.7136, 
      longitude: 46.6753, 
      address: searchAddress || 'الرياض',
      radius: location?.radius || 100 
    });
    setMapKey(prev => prev + 1);
  };

  // اختيار مدينة من القائمة
  const selectCity = (cityName) => {
    const coords = saudiCities[cityName];
    onChange({ ...coords, address: cityName, radius: location?.radius || 100 });
    setSearchAddress(cityName);
    setMapKey(prev => prev + 1);
  };

  // تحديث الموقع من النقر على الخريطة
  const handleMapClick = (lat, lng) => {
    onChange({ 
      ...location, 
      latitude: lat, 
      longitude: lng 
    });
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      
      {/* البحث عن العنوان */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MapPin className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400`} />
          <input
            type="text"
            value={searchAddress}
            onChange={(e) => setSearchAddress(e.target.value)}
            placeholder={t('schedule.enterAddress') || 'أدخل اسم المدينة أو العنوان'}
            className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none text-sm`}
            data-testid="location-search-input"
          />
        </div>
        <button
          type="button"
          onClick={searchLocation}
          className="px-4 py-2 bg-gold text-white rounded-lg hover:bg-gold-dark transition-colors text-sm"
          data-testid="location-search-btn"
        >
          {t('btn.search') || 'بحث'}
        </button>
      </div>

      {/* قائمة المدن السريعة */}
      <div className="flex flex-wrap gap-2">
        {Object.keys(saudiCities).slice(0, 8).map((city) => (
          <button
            key={city}
            type="button"
            onClick={() => selectCity(city)}
            className={`px-3 py-1 text-xs rounded-full border transition-all ${
              location?.address === city 
                ? 'bg-gold text-white border-gold' 
                : 'bg-white text-gray-600 border-gray-300 hover:border-gold hover:text-gold'
            }`}
            data-testid={`city-btn-${city}`}
          >
            {city}
          </button>
        ))}
      </div>

      {/* إحداثيات الموقع */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('schedule.latitude') || 'خط العرض'}</label>
          <input
            type="number"
            step="0.0001"
            value={location?.latitude || ''}
            onChange={(e) => handleCoordinateChange('latitude', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none text-sm"
            placeholder="24.7136"
            data-testid="latitude-input"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('schedule.longitude') || 'خط الطول'}</label>
          <input
            type="number"
            step="0.0001"
            value={location?.longitude || ''}
            onChange={(e) => handleCoordinateChange('longitude', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none text-sm"
            placeholder="46.6753"
            data-testid="longitude-input"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('schedule.radius') || 'النطاق (متر)'}</label>
          <input
            type="number"
            value={location?.radius || 100}
            onChange={(e) => handleCoordinateChange('radius', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none text-sm"
            placeholder="100"
            data-testid="radius-input"
          />
        </div>
      </div>

      {/* خريطة تفاعلية باستخدام OpenStreetMap */}
      {location?.latitude && location?.longitude && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setShowMap(!showMap)}
            className="text-sm text-gold hover:underline flex items-center gap-1"
            data-testid="toggle-map-btn"
          >
            <MapPin className="w-4 h-4" />
            {showMap ? (t('schedule.hideMap') || 'إخفاء الخريطة') : (t('schedule.showMap') || 'عرض الخريطة')}
          </button>
          
          {showMap && (
            <div className="relative rounded-lg overflow-hidden border h-64" data-testid="map-container">
              <iframe
                key={mapKey}
                title="Location Map"
                width="100%"
                height="100%"
                frameBorder="0"
                scrolling="no"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${location.longitude - 0.01},${location.latitude - 0.01},${location.longitude + 0.01},${location.latitude + 0.01}&layer=mapnik&marker=${location.latitude},${location.longitude}`}
                style={{ border: 0 }}
              />
            </div>
          )}
          
          {/* معلومات الموقع */}
          <div className="bg-gray-50 rounded-lg px-3 py-2 text-xs flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gold" />
              <span className="font-medium">{location.address || t('schedule.selectedLocation') || 'الموقع المحدد'}</span>
            </div>
            <div className="text-gray-500">
              {t('schedule.radius') || 'النطاق'}: {location.radius}m
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function EmployeeScheduleManagement() {
  const navigate = useNavigate();
  const { t, isRTL, dir } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('schedules'); // 'schedules' | 'attendance' | 'reports'
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterScheduleStatus, setFilterScheduleStatus] = useState('all'); // 'all' | 'with' | 'without'

  // بيانات النموذج
  const [formData, setFormData] = useState({
    employee_id: '',
    schedule_type: 'single',
    shift1: { start_time: '08:00', end_time: '16:00', reminder_before: 15 },
    shift1_location: { latitude: 24.7136, longitude: 46.6753, radius: 100, address: '' },
    shift2: { start_time: '17:00', end_time: '21:00', reminder_before: 15 },
    shift2_location: { latitude: 24.7136, longitude: 46.6753, radius: 100, address: '' },
    working_days: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday'],
    notifications_enabled: true,
    reminder_minutes_before: 15,
    checkout_reminder: true
  });

  const daysOfWeek = [
    { id: 'sunday', labelKey: 'day.sunday' },
    { id: 'monday', labelKey: 'day.monday' },
    { id: 'tuesday', labelKey: 'day.tuesday' },
    { id: 'wednesday', labelKey: 'day.wednesday' },
    { id: 'thursday', labelKey: 'day.thursday' },
    { id: 'friday', labelKey: 'day.friday' },
    { id: 'saturday', labelKey: 'day.saturday' }
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [employeesRes, schedulesRes] = await Promise.all([
        axios.get(`${API}/employees/list`, { headers }),
        axios.get(`${API}/employee-schedules`, { headers })
      ]);

      setEmployees(employeesRes.data.employees || []);
      setSchedules(schedulesRes.data.schedules || []);
      
      // محاولة جلب سجلات الحضور
      try {
        const attendanceRes = await axios.get(`${API}/attendance/records`, { headers });
        setAttendanceRecords(attendanceRes.data.records || []);
      } catch {
        // إذا لم يكن API الحضور متاحاً، استخدم بيانات وهمية للعرض
        setAttendanceRecords([]);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(t('msg.operationFailed') || 'فشل في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // فتح نافذة إضافة جدول جديد
  const openNewScheduleModal = () => {
    setSelectedEmployee(null);
    setFormData({
      employee_id: '',
      schedule_type: 'single',
      shift1: { start_time: '08:00', end_time: '16:00', reminder_before: 15 },
      shift1_location: { latitude: 24.7136, longitude: 46.6753, radius: 100, address: 'الرياض' },
      shift2: { start_time: '17:00', end_time: '21:00', reminder_before: 15 },
      shift2_location: { latitude: 24.7136, longitude: 46.6753, radius: 100, address: '' },
      working_days: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday'],
      notifications_enabled: true,
      reminder_minutes_before: 15,
      checkout_reminder: true
    });
    setShowModal(true);
  };

  const openAddModal = (employee) => {
    setSelectedEmployee(employee);
    const existingSchedule = schedules.find(s => s.employee_id === employee.id);
    
    if (existingSchedule) {
      setFormData({
        employee_id: employee.id,
        schedule_type: existingSchedule.schedule_type || 'single',
        shift1: existingSchedule.shift1 || { start_time: '08:00', end_time: '16:00', reminder_before: 15 },
        shift1_location: existingSchedule.shift1_location || { latitude: 24.7136, longitude: 46.6753, radius: 100, address: '' },
        shift2: existingSchedule.shift2 || { start_time: '17:00', end_time: '21:00', reminder_before: 15 },
        shift2_location: existingSchedule.shift2_location || { latitude: 24.7136, longitude: 46.6753, radius: 100, address: '' },
        working_days: existingSchedule.working_days || ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday'],
        notifications_enabled: existingSchedule.notifications_enabled ?? true,
        reminder_minutes_before: existingSchedule.reminder_minutes_before || 15,
        checkout_reminder: existingSchedule.checkout_reminder ?? true
      });
    } else {
      setFormData({
        employee_id: employee.id,
        schedule_type: 'single',
        shift1: { start_time: '08:00', end_time: '16:00', reminder_before: 15 },
        shift1_location: { latitude: 24.7136, longitude: 46.6753, radius: 100, address: '' },
        shift2: { start_time: '17:00', end_time: '21:00', reminder_before: 15 },
        shift2_location: { latitude: 24.7136, longitude: 46.6753, radius: 100, address: '' },
        working_days: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday'],
        notifications_enabled: true,
        reminder_minutes_before: 15,
        checkout_reminder: true
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

      const existingSchedule = schedules.find(s => s.employee_id === formData.employee_id);
      
      const payload = {
        ...formData,
        shift2: formData.schedule_type === 'double' ? formData.shift2 : null,
        shift2_location: formData.schedule_type === 'double' ? formData.shift2_location : null
      };

      if (existingSchedule) {
        await axios.put(`${API}/employee-schedules/${formData.employee_id}`, payload, { headers });
      } else {
        await axios.post(`${API}/employee-schedules`, payload, { headers });
      }

      setSuccess(t('msg.savedSuccessfully'));
      setShowModal(false);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving schedule:', err);
      setError(err.response?.data?.detail || t('msg.operationFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (employeeId) => {
    if (!window.confirm(t('msg.deleteConfirm'))) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/employee-schedules/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(t('msg.deletedSuccess'));
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(t('msg.operationFailed'));
    }
  };

  const sendReminder = async (employeeId, employeeName) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/employee-notifications/send`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          employee_id: employeeId,
          notification_type: 'manual_reminder',
          title: t('schedule.reminderTitle'),
          message: t('schedule.reminderMessage')
        }
      });
      setSuccess(`${t('schedule.reminderSent')} ${employeeName}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(t('msg.operationFailed'));
    }
  };

  const toggleWorkingDay = (day) => {
    const days = [...formData.working_days];
    const index = days.indexOf(day);
    if (index > -1) {
      days.splice(index, 1);
    } else {
      days.push(day);
    }
    setFormData({ ...formData, working_days: days });
  };

  const getEmployeeSchedule = (employeeId) => {
    return schedules.find(s => s.employee_id === employeeId);
  };

  // تصفية الموظفين بناءً على البحث والفلاتر
  const filteredEmployees = employees.filter(employee => {
    // تصفية البحث
    const matchesSearch = !searchTerm || 
      employee.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // تصفية حالة الجدول
    const hasSchedule = getEmployeeSchedule(employee.id);
    const matchesFilter = 
      filterScheduleStatus === 'all' ||
      (filterScheduleStatus === 'with' && hasSchedule) ||
      (filterScheduleStatus === 'without' && !hasSchedule);
    
    return matchesSearch && matchesFilter;
  });

  // الموظفين بدون جدول (لقائمة الاختيار في النافذة)
  const employeesWithoutSchedule = employees.filter(emp => !getEmployeeSchedule(emp.id));

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center" dir={dir}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('msg.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" dir={dir}>
      {/* Header */}
      <header className="bg-primary text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowRight className="w-5 h-5" />
              {t('workSchedule.backToDashboard') || 'العودة للوحة التحكم'}
            </button>
            <h1 className="text-xl font-heading font-bold">{t('schedule.title') || 'إدارة دوام الموظفين'}</h1>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="minimal" className="text-white" />
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              {t('btn.refresh') || 'تحديث'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {/* رسائل النجاح والخطأ */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700">{success}</span>
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {/* التبويبات */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('schedules')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                activeTab === 'schedules'
                  ? 'bg-gold/10 text-gold border-b-2 border-gold'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
              data-testid="tab-schedules"
            >
              <div className="flex items-center justify-center gap-2">
                <Clock className="w-5 h-5" />
                {t('schedule.schedulesTab') || 'جداول الدوام'}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('attendance')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                activeTab === 'attendance'
                  ? 'bg-gold/10 text-gold border-b-2 border-gold'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
              data-testid="tab-attendance"
            >
              <div className="flex items-center justify-center gap-2">
                <Calendar className="w-5 h-5" />
                {t('schedule.attendanceTab') || 'سجل الحضور'}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                activeTab === 'reports'
                  ? 'bg-gold/10 text-gold border-b-2 border-gold'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
              data-testid="tab-reports"
            >
              <div className="flex items-center justify-center gap-2">
                <FileText className="w-5 h-5" />
                {t('schedule.reportsTab') || 'التقارير'}
              </div>
            </button>
          </div>
        </div>

        {/* محتوى التبويب الأول: جداول الدوام */}
        {activeTab === 'schedules' && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* شريط الأدوات */}
            <div className="p-4 border-b bg-gray-50 flex flex-wrap items-center justify-between gap-4">
              <h2 className="font-semibold text-primary flex items-center gap-2">
                <Users className="w-5 h-5" />
                {t('schedule.employeesList') || 'قائمة الموظفين'} ({employees.length})
              </h2>
              
              <div className="flex items-center gap-3">
                {/* البحث */}
                <div className="relative">
                  <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400`} />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('schedule.searchEmployee') || 'بحث عن موظف...'}
                    className={`${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none text-sm w-48`}
                    data-testid="search-employee-input"
                  />
                </div>
                
                {/* تصفية */}
                <select
                  value={filterScheduleStatus}
                  onChange={(e) => setFilterScheduleStatus(e.target.value)}
                  className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none text-sm"
                  data-testid="filter-schedule-status"
                >
                  <option value="all">{t('schedule.filterAll') || 'الكل'}</option>
                  <option value="with">{t('schedule.filterWithSchedule') || 'لديهم جدول'}</option>
                  <option value="without">{t('schedule.filterWithoutSchedule') || 'بدون جدول'}</option>
                </select>
                
                {/* زر إضافة جدول جديد */}
                <button
                  onClick={openNewScheduleModal}
                  className="flex items-center gap-2 px-4 py-2 bg-gold text-white rounded-lg hover:bg-gold-dark transition-colors"
                  data-testid="add-new-schedule-btn"
                >
                  <Plus className="w-5 h-5" />
                  {t('schedule.addNewSchedule') || 'إضافة جدول جديد'}
                </button>
              </div>
            </div>

            {/* قائمة الموظفين */}
            <div className="divide-y">
              {filteredEmployees.map((employee) => {
                const schedule = getEmployeeSchedule(employee.id);
                return (
                  <div key={employee.id} className="p-4 hover:bg-gray-50 transition-colors" data-testid={`employee-row-${employee.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                          <User className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-primary">{employee.full_name}</h3>
                          <p className="text-sm text-gray-500">{employee.email}</p>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                            {t(`employees.${employee.role}`) || employee.role}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        {schedule ? (
                          <div className="text-sm space-y-1">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">
                                {schedule.shift1?.start_time} - {schedule.shift1?.end_time}
                              </span>
                              {schedule.schedule_type === 'double' && (
                                <span className="text-gray-400">
                                  | {schedule.shift2?.start_time} - {schedule.shift2?.end_time}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-500 text-xs">
                                {schedule.shift1_location?.address || t('schedule.locationSet') || 'تم تحديد الموقع'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              {schedule.notifications_enabled ? (
                                <Bell className="w-4 h-4 text-green-500" />
                              ) : (
                                <Bell className="w-4 h-4 text-gray-300" />
                              )}
                              <span className={`text-xs ${schedule.notifications_enabled ? 'text-green-600' : 'text-gray-400'}`}>
                                {schedule.notifications_enabled ? (t('schedule.notificationsOn') || 'التنبيهات مفعلة') : (t('schedule.notificationsOff') || 'التنبيهات معطلة')}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">{t('schedule.noSchedule') || 'لا يوجد جدول'}</span>
                        )}

                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openAddModal(employee)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title={schedule ? (t('btn.edit') || 'تعديل') : (t('btn.add') || 'إضافة')}
                            data-testid={`edit-schedule-btn-${employee.id}`}
                          >
                            {schedule ? <Edit3 className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                          </button>
                          {schedule && (
                            <>
                              <button
                                onClick={() => sendReminder(employee.id, employee.full_name)}
                                className="p-2 text-gold hover:bg-gold/10 rounded-lg transition-colors"
                                title={t('schedule.sendReminder') || 'إرسال تذكير'}
                                data-testid={`send-reminder-btn-${employee.id}`}
                              >
                                <Bell className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDelete(employee.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title={t('btn.delete') || 'حذف'}
                                data-testid={`delete-schedule-btn-${employee.id}`}
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {filteredEmployees.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>{t('schedule.noEmployeesFound') || 'لم يتم العثور على موظفين'}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* محتوى التبويب الثاني: سجل الحضور */}
        {activeTab === 'attendance' && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h2 className="font-semibold text-primary flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {t('schedule.attendanceRecords') || 'سجلات الحضور'}
              </h2>
            </div>
            
            <div className="p-6">
              {attendanceRecords.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-right">{t('schedule.employee') || 'الموظف'}</th>
                        <th className="px-4 py-3 text-right">{t('schedule.date') || 'التاريخ'}</th>
                        <th className="px-4 py-3 text-right">{t('schedule.checkIn') || 'وقت الحضور'}</th>
                        <th className="px-4 py-3 text-right">{t('schedule.checkOut') || 'وقت الانصراف'}</th>
                        <th className="px-4 py-3 text-right">{t('schedule.status') || 'الحالة'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {attendanceRecords.map((record, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3">{record.employee_name}</td>
                          <td className="px-4 py-3">{record.date}</td>
                          <td className="px-4 py-3">{record.check_in || '-'}</td>
                          <td className="px-4 py-3">{record.check_out || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              record.status === 'present' ? 'bg-green-100 text-green-700' :
                              record.status === 'late' ? 'bg-yellow-100 text-yellow-700' :
                              record.status === 'absent' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {record.status === 'present' ? (t('schedule.present') || 'حاضر') :
                               record.status === 'late' ? (t('schedule.late') || 'متأخر') :
                               record.status === 'absent' ? (t('schedule.absent') || 'غائب') :
                               record.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg mb-2">{t('schedule.noAttendanceRecords') || 'لا توجد سجلات حضور'}</p>
                  <p className="text-sm">{t('schedule.attendanceWillAppear') || 'ستظهر سجلات الحضور هنا عند تسجيل الموظفين'}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* محتوى التبويب الثالث: التقارير */}
        {activeTab === 'reports' && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
              <h2 className="font-semibold text-primary flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {t('schedule.reports') || 'التقارير'}
              </h2>
              <button
                className="flex items-center gap-2 px-4 py-2 bg-gold text-white rounded-lg hover:bg-gold-dark transition-colors"
                data-testid="export-report-btn"
              >
                <Download className="w-4 h-4" />
                {t('schedule.exportReport') || 'تصدير التقرير'}
              </button>
            </div>
            
            <div className="p-6">
              {/* ملخص الإحصائيات */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-700">{employees.length}</p>
                      <p className="text-xs text-blue-600">{t('schedule.totalEmployees') || 'إجمالي الموظفين'}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-green-50 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-700">{schedules.length}</p>
                      <p className="text-xs text-green-600">{t('schedule.activeSchedules') || 'جداول نشطة'}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-yellow-50 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-yellow-700">{employees.length - schedules.length}</p>
                      <p className="text-xs text-yellow-600">{t('schedule.withoutSchedule') || 'بدون جدول'}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-purple-50 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Bell className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-purple-700">
                        {schedules.filter(s => s.notifications_enabled).length}
                      </p>
                      <p className="text-xs text-purple-600">{t('schedule.notificationsActive') || 'تنبيهات مفعلة'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* جدول ملخص الجداول */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-right">{t('schedule.employee') || 'الموظف'}</th>
                      <th className="px-4 py-3 text-right">{t('schedule.scheduleType') || 'نوع الدوام'}</th>
                      <th className="px-4 py-3 text-right">{t('schedule.firstShift') || 'الدوام الأول'}</th>
                      <th className="px-4 py-3 text-right">{t('schedule.workLocation') || 'موقع العمل'}</th>
                      <th className="px-4 py-3 text-right">{t('schedule.workingDays') || 'أيام العمل'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {schedules.map((schedule, idx) => {
                      const employee = employees.find(e => e.id === schedule.employee_id);
                      return (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">{schedule.employee_name || employee?.full_name || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              schedule.schedule_type === 'double' 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {schedule.schedule_type === 'double' 
                                ? (t('schedule.doubleShift') || 'دوامين') 
                                : (t('schedule.singleShift') || 'دوام واحد')}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {schedule.shift1?.start_time} - {schedule.shift1?.end_time}
                          </td>
                          <td className="px-4 py-3 text-gray-500">
                            {schedule.shift1_location?.address || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-gray-500">
                              {schedule.working_days?.length || 0} {t('schedule.days') || 'أيام'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                
                {schedules.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>{t('schedule.noSchedulesYet') || 'لا توجد جداول بعد'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* نافذة إضافة/تعديل الدوام */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between bg-gray-50">
              <h3 className="font-semibold text-lg text-primary flex items-center gap-2">
                <Settings className="w-5 h-5" />
                {selectedEmployee 
                  ? `${t('schedule.scheduleFor') || 'جدول دوام'} ${selectedEmployee?.full_name}`
                  : (t('schedule.addNewSchedule') || 'إضافة جدول جديد')}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                data-testid="close-modal-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* اختيار الموظف (فقط عند إضافة جدول جديد بدون تحديد موظف) */}
              {!selectedEmployee && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('schedule.selectEmployee') || 'اختر الموظف'} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                    data-testid="select-employee-dropdown"
                  >
                    <option value="">{t('schedule.selectEmployeePlaceholder') || '-- اختر موظفاً --'}</option>
                    {employeesWithoutSchedule.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.full_name} - {t(`employees.${emp.role}`) || emp.role}
                      </option>
                    ))}
                  </select>
                  {employeesWithoutSchedule.length === 0 && (
                    <p className="mt-2 text-sm text-yellow-600">
                      {t('schedule.allEmployeesHaveSchedules') || 'جميع الموظفين لديهم جداول بالفعل'}
                    </p>
                  )}
                </div>
              )}
              {/* نوع الدوام */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">{t('schedule.scheduleType')}</label>
                <div className="flex gap-4">
                  <label className={`flex-1 flex items-center justify-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.schedule_type === 'single' 
                      ? 'border-gold bg-gold/10 text-gold' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="schedule_type"
                      value="single"
                      checked={formData.schedule_type === 'single'}
                      onChange={(e) => setFormData({ ...formData, schedule_type: e.target.value })}
                      className="sr-only"
                    />
                    <Sun className="w-6 h-6" />
                    <div>
                      <span className="font-semibold block">{t('schedule.singleShift')}</span>
                      <span className="text-xs text-gray-500">{t('schedule.singleShiftDesc')}</span>
                    </div>
                  </label>
                  <label className={`flex-1 flex items-center justify-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.schedule_type === 'double' 
                      ? 'border-gold bg-gold/10 text-gold' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="schedule_type"
                      value="double"
                      checked={formData.schedule_type === 'double'}
                      onChange={(e) => setFormData({ ...formData, schedule_type: e.target.value })}
                      className="sr-only"
                    />
                    <div className="flex">
                      <Sun className="w-5 h-5" />
                      <Moon className="w-5 h-5 -mr-1" />
                    </div>
                    <div>
                      <span className="font-semibold block">{t('schedule.doubleShift')}</span>
                      <span className="text-xs text-gray-500">{t('schedule.doubleShiftDesc')}</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* الدوام الأول */}
              <div className="bg-blue-50 rounded-xl p-4 space-y-4">
                <h4 className="font-semibold text-blue-700 flex items-center gap-2">
                  <Sun className="w-5 h-5" />
                  {t('schedule.firstShift')}
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('schedule.startTime')}</label>
                    <input
                      type="time"
                      value={formData.shift1.start_time}
                      onChange={(e) => setFormData({
                        ...formData,
                        shift1: { ...formData.shift1, start_time: e.target.value }
                      })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('schedule.endTime')}</label>
                    <input
                      type="time"
                      value={formData.shift1.end_time}
                      onChange={(e) => setFormData({
                        ...formData,
                        shift1: { ...formData.shift1, end_time: e.target.value }
                      })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                    />
                  </div>
                </div>
                <LocationPicker
                  location={formData.shift1_location}
                  onChange={(loc) => setFormData({ ...formData, shift1_location: loc })}
                  label={t('schedule.workLocation')}
                />
              </div>

              {/* الدوام الثاني */}
              {formData.schedule_type === 'double' && (
                <div className="bg-purple-50 rounded-xl p-4 space-y-4">
                  <h4 className="font-semibold text-purple-700 flex items-center gap-2">
                    <Moon className="w-5 h-5" />
                    {t('schedule.secondShift')}
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('schedule.startTime')}</label>
                      <input
                        type="time"
                        value={formData.shift2.start_time}
                        onChange={(e) => setFormData({
                          ...formData,
                          shift2: { ...formData.shift2, start_time: e.target.value }
                        })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('schedule.endTime')}</label>
                      <input
                        type="time"
                        value={formData.shift2.end_time}
                        onChange={(e) => setFormData({
                          ...formData,
                          shift2: { ...formData.shift2, end_time: e.target.value }
                        })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                      />
                    </div>
                  </div>
                  <LocationPicker
                    location={formData.shift2_location}
                    onChange={(loc) => setFormData({ ...formData, shift2_location: loc })}
                    label={t('schedule.workLocation')}
                  />
                </div>
              )}

              {/* أيام العمل */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">{t('schedule.workingDays')}</label>
                <div className="flex flex-wrap gap-2">
                  {daysOfWeek.map((day) => (
                    <button
                      key={day.id}
                      type="button"
                      onClick={() => toggleWorkingDay(day.id)}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${
                        formData.working_days.includes(day.id)
                          ? 'border-gold bg-gold text-white'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {t(day.labelKey)}
                    </button>
                  ))}
                </div>
              </div>

              {/* إعدادات التنبيهات */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  {t('schedule.notificationSettings')}
                </h4>
                
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.notifications_enabled}
                      onChange={(e) => setFormData({ ...formData, notifications_enabled: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-gold focus:ring-gold"
                    />
                    <span className="text-gray-700">{t('schedule.enableNotifications')}</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.checkout_reminder}
                      onChange={(e) => setFormData({ ...formData, checkout_reminder: e.target.checked })}
                      className="w-5 h-5 rounded border-gray-300 text-gold focus:ring-gold"
                    />
                    <span className="text-gray-700">{t('schedule.checkoutReminder')}</span>
                  </label>

                  <div className="flex items-center gap-3">
                    <label className="text-gray-700">{t('schedule.reminderBefore')}</label>
                    <select
                      value={formData.reminder_minutes_before}
                      onChange={(e) => setFormData({ ...formData, reminder_minutes_before: parseInt(e.target.value) })}
                      className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                    >
                      <option value={5}>5 {t('time.minutes')}</option>
                      <option value={10}>10 {t('time.minutes')}</option>
                      <option value={15}>15 {t('time.minutes')}</option>
                      <option value={30}>30 {t('time.minutes')}</option>
                      <option value={60}>60 {t('time.minutes')}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {t('btn.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-gold text-white rounded-lg hover:bg-gold-dark transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {t('msg.saving')}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {t('btn.save')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
